import React, { useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowRight, Plus, Sparkles, X } from 'lucide-react';

/**
 * Add Relationship dialog — extracted from SemanticModelsPage.
 *
 * Owns no state; all controlled by the parent. Calls the parent's
 * `onAdd` handler (which performs the API call and updates relationships).
 *
 * Includes a lightweight auto-suggest pass: when both source + target
 * entities are picked and the join keys are still empty, the dialog
 * scans the two attribute lists for a likely match (exact name → near
 * match → `_id` suffix) and pre-selects it. Suggestions never overwrite
 * a value the user has already chosen.
 */

// ────────────────────────────────────────────────────────────────────────
// Join-key auto-suggest heuristic
// ────────────────────────────────────────────────────────────────────────
// Why these tiers? Real-world models alternate between snake_case and
// camelCase, and analysts often rename foreign keys (`customer_id` vs
// `cust_key`). We try the most specific signal first and stop at the
// first non-empty hit so we never propose a low-confidence match when a
// high-confidence one exists.

const _norm = (s) => (s || '').toLowerCase().replace(/[_\-\s]/g, '');

function findBestJoinMatch(srcAttrs, tgtAttrs) {
  if (!srcAttrs?.length || !tgtAttrs?.length) return null;
  const srcByExact = new Map(srcAttrs.map(a => [a.name, a]));
  const srcByNorm = new Map(srcAttrs.map(a => [_norm(a.name), a]));

  // Tier 1: exact name match (most reliable — `policy_id` ↔ `policy_id`).
  for (const t of tgtAttrs) {
    if (srcByExact.has(t.name)) {
      return { src: srcByExact.get(t.name).name, tgt: t.name, confidence: 'exact' };
    }
  }
  // Tier 2: name match ignoring case + separators (handles camelCase vs snake_case).
  for (const t of tgtAttrs) {
    const hit = srcByNorm.get(_norm(t.name));
    if (hit) {
      return { src: hit.name, tgt: t.name, confidence: 'near' };
    }
  }
  // Tier 3: shared `_id` suffix — `customer_id` ↔ `cust_id`. Lower confidence
  // because the shared suffix doesn't prove the entities match; only fall
  // through to it when no better signal exists. We require the prefix to
  // also share at least 3 normalized characters so `order_id` doesn't
  // wrongly latch onto `widget_id`.
  for (const t of tgtAttrs) {
    const tn = _norm(t.name);
    if (!tn.endsWith('id')) continue;
    const tprefix = tn.slice(0, -2);
    for (const s of srcAttrs) {
      const sn = _norm(s.name);
      if (!sn.endsWith('id')) continue;
      const sprefix = sn.slice(0, -2);
      if (sprefix.length >= 3 && (tprefix.includes(sprefix) || sprefix.includes(tprefix))) {
        return { src: s.name, tgt: t.name, confidence: 'suffix' };
      }
    }
  }
  return null;
}

export function AddRelationshipDialog({
  open, onOpenChange,
  entities,
  relName, setRelName,
  relSource, setRelSource,
  relTarget, setRelTarget,
  mappings, setMappings,
  relCardinality, setRelCardinality,
  saving,
  onAdd,
}) {
  const setMapping = (idx, key, val) =>
    setMappings(prev => prev.map((m, i) => (i === idx ? { ...m, [key]: val } : m)));
  const addMapping = () => setMappings(prev => [...prev, { source: '', target: '' }]);
  const removeMapping = (idx) => setMappings(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  const firstMap = mappings[0] || { source: '', target: '' };
  const sourceEntity = entities.find(en => en.entity_id === relSource);
  const targetEntity = entities.find(en => en.entity_id === relTarget);
  // Memoize attribute slices so the suggestion useMemo below doesn't see a
  // fresh array identity on every parent re-render and re-fire unnecessarily.
  const sourceAttrs = useMemo(() => sourceEntity ? (sourceEntity.attributes || []) : [], [sourceEntity]);
  const targetAttrs = useMemo(() => targetEntity ? (targetEntity.attributes || []) : [], [targetEntity]);

  // Recompute the best match whenever source/target change. Cheap (O(N+M)
  // hash lookups) so no need to memo more aggressively.
  const suggestion = useMemo(
    () => findBestJoinMatch(sourceAttrs, targetAttrs),
    [sourceAttrs, targetAttrs]
  );

  // Auto-fill only when the FIRST mapping's join fields are both empty — never
  // clobber a value the user already picked, even if they later swap.
  useEffect(() => {
    if (!open) return;
    if (!suggestion) return;
    const f = mappings[0] || {};
    if (!f.source && !f.target) {
      setMappings(prev => {
        const next = prev.length ? [...prev] : [{ source: '', target: '' }];
        next[0] = { source: suggestion.src, target: suggestion.tgt };
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, relSource, relTarget, suggestion?.src, suggestion?.tgt]);

  return (
    <Dialog open={open} onOpenChange={(v) => {
      onOpenChange(v);
      if (!v) {
        setRelSource(''); setRelTarget(''); setRelName('');
        setMappings([{ source: '', target: '' }]);
      }
    }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Add Relationship</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs text-muted-foreground">Name</Label>
            <Input value={relName} onChange={e => setRelName(e.target.value)} className="rounded-lg bg-muted/50 border-border/50 mt-1 !text-xs" />
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
              <p className="text-[8px] text-emerald-600 dark:text-emerald-400 uppercase tracking-wider font-semibold mb-0.5">Source (FROM)</p>
              <Select value={relSource} onValueChange={setRelSource}>
                <SelectTrigger className="h-7 !text-xs bg-transparent border-0 min-w-0 [&>span]:truncate"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{entities.map(e => <SelectItem key={e.entity_id} value={e.entity_id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <ArrowRight className="w-4 h-4 text-primary flex-shrink-0" />
            <div className="flex-1 min-w-0 p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-center">
              <p className="text-[8px] text-blue-400 uppercase tracking-wider font-semibold mb-0.5">Target (TO)</p>
              <Select value={relTarget} onValueChange={setRelTarget}>
                <SelectTrigger className="h-7 !text-xs bg-transparent border-0 min-w-0 [&>span]:truncate"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{entities.filter(e => e.entity_id !== relSource).map(e => <SelectItem key={e.entity_id} value={e.entity_id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {/* Auto-suggest hint — surfaces only when the picked fields match
              what the heuristic proposed, so users see WHY we filled them. */}
          {suggestion && firstMap.source === suggestion.src && firstMap.target === suggestion.tgt && (
            <div className="flex items-start gap-2 px-2.5 py-1.5 rounded-lg border border-cyan-500/25 bg-cyan-500/5" data-testid="join-suggestion-banner">
              <Sparkles className="w-3 h-3 text-cyan-400 flex-shrink-0 mt-0.5" />
              <p className="text-[10px] text-cyan-200 leading-snug">
                Suggested join keys based on{' '}
                <span className="font-medium">
                  {suggestion.confidence === 'exact' ? 'exact name match' :
                   suggestion.confidence === 'near'  ? 'matching name (case/separator ignored)' :
                                                       'matching `_id` suffix'}
                </span>. Override below if needed.
              </p>
            </div>
          )}
          {/* Join key mappings — a multi-column join ANDs each source↔target
              pair together. Most joins need one row; composite keys add more. */}
          <div className="space-y-2" data-testid="join-mappings">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] text-muted-foreground">Field Mappings (joined with AND)</Label>
              <button
                type="button"
                onClick={addMapping}
                data-testid="add-join-mapping-btn"
                className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              >
                <Plus className="w-3 h-3" />Add mapping
              </button>
            </div>
            {mappings.map((m, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_auto_1fr_auto] items-end gap-2" data-testid={`join-mapping-row-${idx}`}>
                <div className="min-w-0">
                  {idx === 0 && <Label className="text-[10px] text-emerald-600 dark:text-emerald-400">Source Field</Label>}
                  <Select value={m.source} onValueChange={v => setMapping(idx, 'source', v)}>
                    <SelectTrigger data-testid={`join-source-field-${idx}`} className="rounded-lg bg-muted/50 border-border/50 mt-0.5 !text-xs min-w-0 [&>span]:truncate"><SelectValue placeholder="Select field..." /></SelectTrigger>
                    <SelectContent className="max-h-60">{sourceAttrs.map(a => <SelectItem key={a.name} value={a.name}>{a.display_name || a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-primary flex-shrink-0 mb-2.5" />
                <div className="min-w-0">
                  {idx === 0 && <Label className="text-[10px] text-blue-400">Target Field</Label>}
                  <Select value={m.target} onValueChange={v => setMapping(idx, 'target', v)}>
                    <SelectTrigger data-testid={`join-target-field-${idx}`} className="rounded-lg bg-muted/50 border-border/50 mt-0.5 !text-xs min-w-0 [&>span]:truncate"><SelectValue placeholder="Select field..." /></SelectTrigger>
                    <SelectContent className="max-h-60">{targetAttrs.map(a => <SelectItem key={a.name} value={a.name}>{a.display_name || a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <button
                  type="button"
                  onClick={() => removeMapping(idx)}
                  disabled={mappings.length <= 1}
                  data-testid={`remove-join-mapping-${idx}`}
                  className="mb-1 p-1 rounded text-muted-foreground hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Remove mapping"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Join Type</Label>
              <Select defaultValue="inner" onValueChange={() => {}}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-1 !text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inner">Inner</SelectItem>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                  <SelectItem value="full">Full</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Cardinality</Label>
              <Select value={relCardinality} onValueChange={setRelCardinality}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-1 !text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_to_one">1:1</SelectItem>
                  <SelectItem value="one_to_many">1:N</SelectItem>
                  <SelectItem value="many_to_one">N:1</SelectItem>
                  <SelectItem value="many_to_many">N:N</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            onClick={onAdd}
            disabled={saving || !relSource || !relTarget || !relName.trim()}
            className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95"
          >
            <Plus className="w-4 h-4 mr-2" />Add Relationship
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
